import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "Components/Hooks/hooks";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import { IconName } from "Components/Icons/Icon";

interface ProfileCompleteProtectedProps {
  children: React.ReactNode;
}

type RestrictionType =
  | "SUBSCRIPTION_REQUIRED"
  | "BANK_ACCOUNT_REQUIRED"
  | "SIGNATURE_REQUIRED"
  | "BANK_AND_SIGNATURE_REQUIRED"
  | null;

const RESTRICTION_CONFIG: Record<
  Exclude<RestrictionType, null>,
  {
    iconName: IconName;
    title: string;
    description: string;
    buttonText: string;
    navigateTo: string;
    navigateState?: object;
  }
> = {
  SUBSCRIPTION_REQUIRED: {
    iconName: "SubscriptionRequired",
    title: "Subscription Required",
    description:
      "To continue using EaziQuote, please activate or renew your subscription.",
    buttonText: "Activate Subscription",
    navigateTo: "/subscribe-plan",
  },
  BANK_ACCOUNT_REQUIRED: {
    iconName: "Info",
    title: "Add Bank Details",
    description:
      "Including bank details makes it easier for clients to pay you without follow-ups.",
    buttonText: "Add Bank Details",
    navigateTo: "/payments-and-invoicing",
  },
  SIGNATURE_REQUIRED: {
    iconName: "Info",
    title: "Add Your Signature",
    description:
      "Adding a signature helps build trust and makes your quote feel complete and professional.",
    buttonText: "Add Signature",
    navigateTo: "/payments-and-invoicing",
    navigateState: { signatureMissing: true },
  },
  BANK_AND_SIGNATURE_REQUIRED: {
    iconName: "Info",
    title: "Complete Your Setup",
    description:
      "Add your bank details and signature to ensure your quotes look professional and include payment information.",
    buttonText: "Complete Setup",
    navigateTo: "/payments-and-invoicing",
  },
};

const ProfileCompleteProtected = ({
  children,
}: ProfileCompleteProtectedProps) => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state?.auth?.user);
  const [showModal, setShowModal] = useState(false);
  const [restrictionType, setRestrictionType] = useState<RestrictionType>(null);

  const hasNotSubscribed =
    user?.is_subscription_active === false && user?.is_trial_period === false;
  const hasBankAccount = user?.hasBankAccountDetailAdded;
  const hasSignature = user?.hasSignatureAdded;

  useEffect(() => {
    if (hasNotSubscribed) {
      setRestrictionType("SUBSCRIPTION_REQUIRED");
      setShowModal(true);
    } else if (!hasBankAccount && !hasSignature) {
      setRestrictionType("BANK_AND_SIGNATURE_REQUIRED");
      setShowModal(true);
    } else if (!hasBankAccount) {
      setRestrictionType("BANK_ACCOUNT_REQUIRED");
      setShowModal(true);
    } else if (!hasSignature) {
      setRestrictionType("SIGNATURE_REQUIRED");
      setShowModal(true);
    } else {
      setShowModal(false);
      setRestrictionType(null);
    }
  }, [hasNotSubscribed, hasBankAccount, hasSignature]);

  const handleCancel = () => {
    setShowModal(false);
    // Navigate back or to dashboard
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/dashboard", { replace: true });
    }
  };

  const handleAction = () => {
    if (restrictionType) {
      const config = RESTRICTION_CONFIG[restrictionType];
      navigate(config.navigateTo, {
        replace: true,
        state: config.navigateState,
      });
    }
    setShowModal(false);
  };

  // If there's a restriction, show the modal and don't render children
  if (restrictionType) {
    const config = RESTRICTION_CONFIG[restrictionType];
    return (
      <RestrictedAccessModal
        isOpen={showModal}
        onCancel={handleCancel}
        iconName={config.iconName}
        title={config.title}
        description={config.description}
        buttonText={config.buttonText}
        onButtonClick={handleAction}
      />
    );
  }

  return <>{children}</>;
};

export default ProfileCompleteProtected;
